import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Check, Github, Slack, Code, Bell, Play } from "lucide-react";

const steps = [
  { id: 1, title: "Sign Up", time: "30 sec" },
  { id: 2, title: "Create Organization", time: "20 sec" },
  { id: 3, title: "Connect GitHub", time: "60 sec", mandatory: true },
  { id: 4, title: "Connect Slack", time: "60 sec", mandatory: true },
  { id: 5, title: "Install SDK", time: "2 min" },
  { id: 6, title: "Configure Sentry", time: "2 min" },
  { id: 7, title: "Optional Sessions", time: "1 min" },
  { id: 8, title: "Test Everything", time: "2 min" },
];

export default function Onboarding() {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    companyName: "",
    teamMembers: "",
    framework: "nodejs",
    apiKey: "hlai_" + Math.random().toString(36).substr(2, 9),
  });
  const navigate = useNavigate();

  const nextStep = () => {
    if (currentStep === steps.length) {
      navigate("/dashboard");
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const skipToEnd = () => {
    navigate("/dashboard");
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-4xl">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-heading font-bold">Getting Started</h1>
            <span className="text-sm text-muted-foreground">
              Step {currentStep} of {steps.length}
            </span>
          </div>
          <div className="flex gap-2">
            {steps.map((step) => (
              <div
                key={step.id}
                className={`h-2 flex-1 rounded-full transition-all ${
                  step.id <= currentStep ? "bg-foreground" : "bg-border"
                }`}
              />
            ))}
          </div>
        </div>

        <Card className="p-8 border-2">
          {/* Step 1: Sign Up */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl font-heading font-bold mb-2">Create Your Account</h2>
                <p className="text-muted-foreground">Start fixing bugs automatically in 30 seconds</p>
              </div>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  />
                </div>
                <Button className="w-full" size="lg" onClick={nextStep}>
                  Continue with Email
                </Button>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">Or</span>
                  </div>
                </div>
                <Button variant="outline" className="w-full" size="lg" onClick={nextStep}>
                  <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                  Continue with Google
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Create Organization */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl font-heading font-bold mb-2">Create Organization</h2>
                <p className="text-muted-foreground">Set up your team workspace</p>
              </div>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="company">Company Name</Label>
                  <Input
                    id="company"
                    placeholder="Acme Inc."
                    value={formData.companyName}
                    onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="team">Invite Team Members (Optional)</Label>
                  <Input
                    id="team"
                    placeholder="colleague@company.com, another@company.com"
                    value={formData.teamMembers}
                    onChange={(e) => setFormData({ ...formData, teamMembers: e.target.value })}
                  />
                </div>
                <div className="bg-muted p-4 rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    Your API Key: <code className="font-mono text-foreground">{formData.apiKey}</code>
                  </p>
                </div>
                <Button className="w-full" size="lg" onClick={nextStep}>
                  Continue
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Connect GitHub */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl font-heading font-bold mb-2 flex items-center gap-2">
                  <Github className="w-8 h-8" />
                  Connect GitHub
                  <span className="text-sm font-normal text-destructive">⚠️ MANDATORY</span>
                </h2>
                <p className="text-muted-foreground">
                  We need access to create PRs with fixes
                </p>
              </div>
              <div className="space-y-4">
                <div className="bg-muted p-6 rounded-lg space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-600" />
                    <span>Read repository contents</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-600" />
                    <span>Create pull requests</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-600" />
                    <span>Install webhooks</span>
                  </div>
                </div>
                <Button className="w-full" size="lg" onClick={nextStep}>
                  <Github className="mr-2 w-4 h-4" />
                  Authorize GitHub
                </Button>
              </div>
            </div>
          )}

          {/* Step 4: Connect Slack */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl font-heading font-bold mb-2 flex items-center gap-2">
                  <Slack className="w-8 h-8" />
                  Connect Slack
                  <span className="text-sm font-normal text-destructive">⚠️ MANDATORY</span>
                </h2>
                <p className="text-muted-foreground">
                  Get notified when fixes are ready
                </p>
              </div>
              <div className="space-y-4">
                <div className="bg-muted p-6 rounded-lg space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-600" />
                    <span>Post to #engineering channel</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-600" />
                    <span>Send error notifications</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-600" />
                    <span>Interactive fix approvals</span>
                  </div>
                </div>
                <Button className="w-full" size="lg" onClick={nextStep}>
                  <Slack className="mr-2 w-4 h-4" />
                  Add to Slack
                </Button>
              </div>
            </div>
          )}

          {/* Step 5: Install SDK */}
          {currentStep === 5 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl font-heading font-bold mb-2 flex items-center gap-2">
                  <Code className="w-8 h-8" />
                  Install SDK
                </h2>
                <p className="text-muted-foreground">Add Healion to your codebase</p>
              </div>
              <div className="space-y-4">
                <div>
                  <Label>Choose Framework</Label>
                  <select
                    className="w-full h-10 rounded-md border border-input bg-background px-3 py-2"
                    value={formData.framework}
                    onChange={(e) => setFormData({ ...formData, framework: e.target.value })}
                  >
                    <option value="nodejs">Node.js</option>
                    <option value="python">Python</option>
                    <option value="ruby">Ruby</option>
                    <option value="java">Java</option>
                  </select>
                </div>
                <div className="bg-muted p-4 rounded-lg">
                  <code className="text-sm font-mono">
                    npm install @healion/sdk<br />
                    <br />
                    import Healion from '@healion/sdk';<br />
                    <br />
                    Healion.init('{formData.apiKey}');
                  </code>
                </div>
                <Button className="w-full" size="lg" onClick={nextStep}>
                  Continue
                </Button>
              </div>
            </div>
          )}

          {/* Step 6: Configure Sentry */}
          {currentStep === 6 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl font-heading font-bold mb-2 flex items-center gap-2">
                  <Bell className="w-8 h-8" />
                  Configure Sentry
                </h2>
                <p className="text-muted-foreground">Connect your error monitoring</p>
              </div>
              <div className="space-y-4">
                <div className="bg-muted p-4 rounded-lg">
                  <p className="text-sm font-medium mb-2">Webhook URL:</p>
                  <code className="text-sm font-mono break-all">
                    https://api.healion.ai/webhooks/sentry/{formData.apiKey}
                  </code>
                </div>
                <div className="text-sm text-muted-foreground space-y-2">
                  <p>1. Go to Sentry Settings → Integrations</p>
                  <p>2. Add Internal Integration</p>
                  <p>3. Paste the webhook URL above</p>
                  <p>4. Enable "Error" events</p>
                </div>
                <Button className="w-full" size="lg" onClick={nextStep}>
                  Test Connection
                </Button>
              </div>
            </div>
          )}

          {/* Step 7: Optional Sessions */}
          {currentStep === 7 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl font-heading font-bold mb-2">Session Replay (Optional)</h2>
                <p className="text-muted-foreground">
                  See what users did before the error
                </p>
              </div>
              <div className="space-y-4">
                <Button variant="outline" className="w-full" size="lg">
                  Connect FullStory
                </Button>
                <Button variant="outline" className="w-full" size="lg">
                  Connect LogRocket
                </Button>
                <Button variant="ghost" className="w-full" onClick={nextStep}>
                  Skip for now
                </Button>
              </div>
            </div>
          )}

          {/* Step 8: Test Everything */}
          {currentStep === 8 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl font-heading font-bold mb-2 flex items-center gap-2">
                  <Play className="w-8 h-8" />
                  Test Everything
                </h2>
                <p className="text-muted-foreground">
                  Let's make sure everything works
                </p>
              </div>
              <div className="space-y-4">
                <div className="bg-muted p-4 rounded-lg">
                  <code className="text-sm font-mono">
                    // Trigger a test error<br />
                    throw new Error('Healion test error');
                  </code>
                </div>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full border-2 border-foreground animate-pulse" />
                    <span>Waiting for error...</span>
                  </div>
                </div>
                <Button className="w-full" size="lg" onClick={nextStep}>
                  ✅ Complete Setup
                </Button>
              </div>
            </div>
          )}

          {/* Skip Button */}
          <div className="mt-6 text-center">
            <button
              onClick={skipToEnd}
              className="text-sm text-muted-foreground hover:text-foreground underline"
            >
              Skip to Dashboard
            </button>
          </div>
        </Card>
      </div>
    </div>
  );
}
